import React, { createContext, useContext, useEffect, useRef, useState } from "react";

export type Language = "en" | "zh" | "ja";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 获取初始语言
  const getInitialLanguage = (): Language => {
    try {
      // 首先尝试从 localStorage 获取保存的语言
      const savedLanguage = localStorage.getItem("language") as Language | null;

      if (savedLanguage && ["en", "zh", "ja"].includes(savedLanguage)) {
        return savedLanguage as Language;
      }

      // 如果没有保存的语言，尝试检测浏览器语言
      const browserLang = navigator.language.split("-")[0];
      if (browserLang === "zh") {
        return "zh";
      } else if (browserLang === "ja") {
        return "ja";
      }

      // 默认返回英语
      return "en";
    } catch (error) {
      // 如果出现错误（例如在 SSR 或隐私模式下），默认使用英语
      return "en";
    }
  };

  // 使用函数初始化 language 状态
  const [language, setLanguage] = useState<Language>(getInitialLanguage);
  const isInitialMount = useRef(true);

  // 只在语言改变且不是初次挂载时保存到 localStorage
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      try {
        localStorage.setItem("language", language);
      } catch (error) {
        console.error("Could not save language preference to localStorage", error);
      }
    }

    // 更新 HTML 元素的 lang 属性
    document.documentElement.lang = language;
  }, [language]);

  return <LanguageContext.Provider value={{ language, setLanguage }}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
